import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "../hooks/useFetch";
import {
  createSubscriptionByAdmin,
  renewSubscriptionByAdmin,
  verifyPaymentByAdmin,
  getAllSubscriptionsByAdmin,
} from "../api/subscriptionsApi";
import { getAllPlanByAdmin } from "../api/planApi";
import "./css/Subscriptions.css";
import AccessControl from "../components/AccessControl";
import Loader from "../components/Loader";

// ─── Load Razorpay script dynamically ────────────────────────────────────────
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function SubscriptionsPage() {
  const navigate = useNavigate();

  // ── List state ──────────────────────────────────────────────────────────────
  // ✅ Status filter pills replaced with 3 derived sections below (Active /
  //    Already Expired / Renewal Pending), so we fetch the full list once
  //    instead of a server-side status-filtered page. If the driver base
  //    grows very large, bump this limit or move grouping server-side.
  const { data, loading, error, refetch } = useFetch(
    () => getAllSubscriptionsByAdmin({ status: "", page: 1, limit: 1000 }),
    [],
  );

  const rows =
    data?.subscriptions ?? data?.data ?? (Array.isArray(data) ? data : []);

  // ── Helper: collapse many records down to ONE row per driver ───────────────
  // Keeps the most recent (by startDate) record for each driverId, and tags
  // it with how many total records that driver had in the input list — used
  // to show "+N more" instead of repeating the same driver over and over.
  const dedupeLatestPerDriver = (list) => {
    const byDriver = {};
    list.forEach((r) => {
      const driverId = r.driverId?._id;
      if (!driverId) return;
      if (!byDriver[driverId]) byDriver[driverId] = [];
      byDriver[driverId].push(r);
    });
    return Object.values(byDriver).map((driverRows) => {
      const latest = driverRows.reduce((a, b) =>
        new Date(b.startDate) > new Date(a.startDate) ? b : a,
      );
      return { ...latest, _historyCount: driverRows.length };
    });
  };

  // ── Derived groupings ───────────────────────────────────────────────────────
  // 🟢 Current Plan — ONE row per driver (their live subscription)
  const activeRows = dedupeLatestPerDriver(
    rows.filter((r) => r.isActive === true),
  );

  // 🔴 Already Expired — ONE row per driver (their most recent expired plan);
  //    _historyCount shows how many times they've expired in total, and the
  //    row links to /drivers/:id/history for the full list instead of
  //    repeating every past record inline.
  const expiredRows = dedupeLatestPerDriver(
    rows.filter((r) => r.isActive === false),
  );

  // ⚠️ Renewal Pending — ONE row per driver: their most recent subscription
  //    (by startDate) is not active, meaning nothing valid has replaced it yet
  const notRenewedRows = (() => {
    const latestByDriver = {};
    rows.forEach((r) => {
      const driverId = r.driverId?._id;
      if (!driverId) return;
      const existing = latestByDriver[driverId];
      if (!existing || new Date(r.startDate) > new Date(existing.startDate)) {
        latestByDriver[driverId] = r;
      }
    });
    return Object.values(latestByDriver).filter((r) => r.isActive === false);
  })();

  // ── Pagination (per section) ────────────────────────────────────────────────
  // With up to ~1000 drivers, rendering every row at once is heavy. Each
  // section shows a page of PAGE_SIZE rows with a "Load More" button —
  // cheap to implement, and keeps the DOM light regardless of driver count.
  const PAGE_SIZE = 15;
  const [activeVisible, setActiveVisible] = useState(PAGE_SIZE);
  const [expiredVisible, setExpiredVisible] = useState(PAGE_SIZE);
  const [notRenewedVisible, setNotRenewedVisible] = useState(PAGE_SIZE);

  // ── Plans (fetched once for modal) ─────────────────────────────────────────
  // ✅ getAllPlanByAdmin → /api/plans/superadmin/get — zonePrices field include ஆகுது
  const { data: planData } = useFetch(getAllPlanByAdmin, []);
  const plans = planData?.plans ?? (Array.isArray(planData) ? planData : []);
  // zonePrices array Plan-ல இருக்கு — extra fetch தேவையில்ல
  const activePlans = plans.filter((p) => p.isActive);

  // ── Activate modal state ────────────────────────────────────────────────────
  const [modalDriver, setModalDriver] = useState(null); // { _id, name, status, planId }
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  // driverZoneId: modal open-ல driver-ஓட zoneId store பண்றோம்
  const [driverZoneId, setDriverZoneId] = useState(null);

  const showToast = (msg, ms = 4000) => {
    setToast(msg);
    setTimeout(() => setToast(""), ms);
  };

  const openModal = (row) => {
    const driver = row.driverId;
    setModalDriver({
      _id: driver?._id,
      name: driver?.name || "—",
      phone: driver?.phone || "",
      zoneName: driver?.zoneName || "",
      zoneId: driver?.zoneId || "",
      subStatus: row.isActive ? "active" : "expired",
      currentPlanId: row.planId?._id,
    });
    // ✅ driver.zoneId store பண்றோம் — plans-ல உள்ள zonePrices array-ல lookup பண்ண
    setDriverZoneId(driver?.zoneId || null);
    setSelectedPlanId("");
    setMethod("cash");
  };

  const closeModal = () => {
    setModalDriver(null);
    setSelectedPlanId("");
    setMethod("cash");
    setDriverZoneId(null);
  };

  // ── Activate / Renew handler ────────────────────────────────────────────────
  const handleActivate = async () => {
    if (!selectedPlanId) return showToast("⚠️ Plan select பண்ணுங்க");

    const isRenew = modalDriver.subStatus === "active";
    const apiFn = isRenew
      ? renewSubscriptionByAdmin
      : createSubscriptionByAdmin;

    setBusy(true);
    try {
      const adminId = localStorage.getItem("adminId");

      const res = await apiFn({
        driverId: modalDriver._id,
        planId: selectedPlanId,
        method,
        collectedBy: adminId || undefined, // ✅ logged-in admin
      });

      if (!res?.success)
        throw new Error(res?.error || res?.message || "Failed");

      const d = res.data;

      // ── CASH ───────────────────────────────────────────────────────────────
      if (method === "cash") {
        showToast(
          "✅ Cash payment pending. Admin confirm பண்ணா activate ஆகும்.",
        );
        closeModal();
        refetch();
        return;
      }

      // ── ONLINE: open Razorpay checkout ─────────────────────────────────────
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        showToast("❌ Razorpay load ஆகல. Internet check பண்ணுங்க.");
        setBusy(false);
        return;
      }

      const plan = activePlans.find((p) => p._id === selectedPlanId);

      const options = {
        key: d.razorpayKeyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: d.amount * 100,
        currency: "INR",
        name: "Moovuruli",
        description: `${plan?.name || "Plan"} — ${modalDriver.name}`,
        order_id: d.razorpayOrderId, // ✅ referenceId set here
        prefill: {
          name: modalDriver.name,
          contact: modalDriver.phone,
        },
        theme: { color: "#7c3aed" },

        // ✅ Payment success — verify & activate
        handler: async (response) => {
          try {
            const verifyRes = await verifyPaymentByAdmin({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            if (verifyRes?.success) {
              showToast("✅ Payment successful! Subscription activated.");
              closeModal();
              refetch();
            } else {
              showToast(
                `❌ Verify failed: ${verifyRes?.message || "Unknown error"}`,
              );
            }
          } catch (e) {
            showToast(`❌ Verification error: ${e.message}`);
          } finally {
            setBusy(false);
          }
        },

        modal: {
          ondismiss: () => {
            showToast("ℹ️ Payment cancelled.");
            setBusy(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        showToast(
          `❌ Payment failed: ${resp.error?.description || "Try again"}`,
        );
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      showToast(`❌ ${e.message}`);
      setBusy(false);
    }
  };

  // ── Badge helpers ───────────────────────────────────────────────────────────
  const getBadgeClass = (val) => {
    const map = {
      active: "badge badge-active",
      expired: "badge badge-expired",
      pending: "badge badge-pending",
    };
    return map[val] || "badge badge-default";
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  // ── Reusable row renderer (used by all 3 sections below) ──────────────────
  const renderSubRow = (r) => {
    const end = new Date(r.endDate);
    const soon = r.isActive && end - new Date() < 7 * 24 * 60 * 60 * 1000;

    return (
      <tr
        key={r._id}
        onClick={() => openModal(r)}
        style={{ cursor: "pointer" }}
      >
        {/* Driver */}
        <td
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/drivers/${r.driverId?._id}/history`);
          }}
        >
          <p className="driver-name">{r.driverId?.name || "—"}</p>
          <p className="driver-phone">{r.driverId?.phone || ""}</p>
          {r._historyCount > 1 && (
            <span
              className="badge badge-default"
              style={{
                fontSize: "0.65rem",
                marginTop: "2px",
                display: "inline-block",
              }}
              title="Click the name to view full subscription history"
            >
              📜 {r._historyCount} records — view history
            </span>
          )}
        </td>
        {/* Territory */}
        <td>
          <span className="badge badge-default">
            {r.driverId?.zoneName || "—"}
          </span>
        </td>
        {/* Plan */}
        <td>
          <span className={getBadgeClass(r.planId?.name)}>
            {r.planId?.name || "—"}
          </span>
        </td>

        {/* Start Date */}
        <td>{new Date(r.startDate).toLocaleDateString("en-IN")}</td>

        {/* End Date */}
        <td>
          <span className={soon ? "expires-soon" : ""}>
            {end.toLocaleDateString("en-IN")}
            {soon && <span className="expires-soon-tag"> (expires soon)</span>}
          </span>
        </td>

        {/* Status — real-time isActive, not the possibly-stale status field */}
        <td>
          <span className={getBadgeClass(r.isActive ? "active" : "expired")}>
            {r.isActive ? "active" : "expired"}
          </span>
        </td>

        {/* Amount — paidAmount இருந்தா அதை show பண்றோம் (actual paid),
             இல்லன்னா driver zoneId → plans zonePrices lookup → zone price காட்டுறோம் */}
        <td>
          {(() => {
            if (r.paidAmount != null) {
              return (
                <span className="amount-text">
                  ₹{r.paidAmount.toLocaleString()}
                </span>
              );
            }
            const driverZone = r.driverId?.zoneId;
            const planDoc = plans.find((p) => p._id === r.planId?._id);
            const zoneEntry =
              driverZone && planDoc
                ? planDoc.zonePrices?.find((z) => z.zoneId === driverZone)
                : null;
            const displayAmt = zoneEntry ? zoneEntry.amount : r.planId?.amount;
            const isZone = !!zoneEntry && zoneEntry.amount !== r.planId?.amount;
            return (
              <span
                className="amount-text"
                title={
                  isZone ? `Global: ₹${r.planId?.amount?.toLocaleString()}` : ""
                }
              >
                ₹{displayAmt?.toLocaleString() || "—"}
                {isZone && (
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: "#7c3aed",
                      marginLeft: "3px",
                    }}
                  >
                    📍
                  </span>
                )}
              </span>
            );
          })()}
        </td>

        {/* ✅ Action — Activate / Renew */}
        <td>
          <button
            className={`subs-action-btn ${r.isActive ? "subs-btn-renew" : "subs-btn-activate"}`}
            onClick={(e) => {
              e.stopPropagation();
              openModal(r);
            }}
          >
            {r.isActive ? "🔄 Renew" : "⚡ Activate"}
          </button>
        </td>
      </tr>
    );
  };

  // ── Reusable section (title + count + table or empty state) ───────────────
  const renderSection = (
    title,
    icon,
    sectionRows,
    emptyText,
    visibleCount,
    setVisibleCount,
  ) => {
    const visibleRows = sectionRows.slice(0, visibleCount);
    const hasMore = sectionRows.length > visibleCount;

    return (
      <div className="subs-section" style={{ marginBottom: "2rem" }}>
        <div className="subs-header" style={{ marginBottom: "0.75rem" }}>
          <h2
            style={{
              fontSize: "1.1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
            }}
          >
            {icon} {title}
            <span
              className="badge badge-default"
              style={{ fontSize: "0.75rem", marginLeft: "0.4rem" }}
            >
              {sectionRows.length}
            </span>
          </h2>
        </div>

        {sectionRows.length === 0 ? (
          <div className="subs-empty">
            <p className="subs-empty-icon">📭</p>
            <p className="subs-empty-title">{emptyText}</p>
          </div>
        ) : (
          <>
            <div className="subs-table-card">
              <table className="subs-table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Territory</th>
                    <th>Plan</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>{visibleRows.map(renderSubRow)}</tbody>
              </table>
            </div>

            {hasMore && (
              <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
                <button
                  className="page-btn"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                >
                  Load More ({sectionRows.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="subs-container">
      <AccessControl
        allowedRoles={[
          "SUPERADMIN",
          "ADMIN",
          "HR",
          "NSM",
          "RSM",
          "ASM",
          "TSO",
          "ACCOUNTS",
        ]}
        redirectTo="/404"
      />

      {/* Toast */}
      {toast && <div className="pc-toast">{toast}</div>}

      {/* Header */}
      <div className="subs-header">
        <h1>Subscriptions</h1>
        <p>
          All driver subscriptions — row click பண்ணி plan activate / renew
          பண்ணலாம்
        </p>
      </div>

      {/* Content */}
      {loading ? (
        <Loader />
      ) : error ? (
        <p className="subs-error">{error}</p>
      ) : rows.length === 0 ? (
        <div className="subs-empty">
          <p className="subs-empty-icon">📭</p>
          <p className="subs-empty-title">No subscriptions found</p>
        </div>
      ) : (
        <>
          {renderSection(
            "Current Plan Drivers",
            "🟢",
            activeRows,
            "Current-a active plan-la irukra driver yaarum illa",
            activeVisible,
            setActiveVisible,
          )}

          {renderSection(
            "Renewal Pending — Follow-up List",
            "⚠️",
            notRenewedRows,
            "Renewal pending-la yaarum illa — எல்லா drivers-கும் valid plan இருக்கு 🎉",
            notRenewedVisible,
            setNotRenewedVisible,
          )}

          {renderSection(
            "Already Expired (History)",
            "🔴",
            expiredRows,
            "Expired records யாரும் இல்ல",
            expiredVisible,
            setExpiredVisible,
          )}
        </>
      )}

      {/* ═══════════ ACTIVATE / RENEW MODAL ═══════════ */}
      {modalDriver && (
        <div className="pc-modal-overlay" onClick={closeModal}>
          <div className="pc-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="pc-modal-header">
              <h3>
                {modalDriver.subStatus === "active"
                  ? "🔄 Renew Subscription"
                  : "⚡ Activate Subscription"}
              </h3>
              <button className="pc-modal-close" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="pc-modal-body">
              {/* Driver info */}
              <div className="pc-detail-card">
                <div className="pc-detail-row">
                  <span className="pc-detail-label">Driver</span>
                  <span className="pc-detail-value">{modalDriver.name}</span>
                </div>
                <div className="pc-detail-row">
                  <span className="pc-detail-label">Phone</span>
                  <span className="pc-detail-value">{modalDriver.phone}</span>
                </div>
                <div className="pc-detail-row">
                  <span className="pc-detail-label">Territory</span>
                  <span className="pc-detail-value">
                    {modalDriver.zoneName || "—"}
                  </span>
                </div>
                <div className="pc-detail-row">
                  <span className="pc-detail-label">Current Status</span>
                  <span
                    className={`badge ${getBadgeClass(modalDriver.subStatus)}`}
                  >
                    {modalDriver.subStatus}
                  </span>
                </div>
              </div>

              {/* Plan Select */}
              <label
                className="pc-notes-label"
                style={{ marginTop: "1rem", display: "block" }}
              >
                Select Plan <span className="pc-required">*</span>
              </label>
              <select
                className="pc-notes-input"
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                style={{
                  padding: "0.5rem",
                  borderRadius: "6px",
                  width: "100%",
                }}
              >
                <option value="">-- Plan தேர்வு செய்யுங்க --</option>
                {activePlans.map((p) => {
                  const isTrial = /trai[ll]/i.test(p.name);
                  // ✅ plan.zonePrices array-ல driver-ஓட zoneId-ஐ directly lookup பண்றோம்
                  // Extra API call வேண்டாம் — getAllPlanByAdmin already zonePrices return பண்றது
                  const zoneOverride = driverZoneId
                    ? p.zonePrices?.find((z) => z.zoneId === driverZoneId)
                    : null;
                  const effectiveAmt = zoneOverride
                    ? zoneOverride.amount
                    : p.amount;
                  const isOverridden =
                    !!zoneOverride && zoneOverride.amount !== p.amount;
                  return (
                    <option key={p._id} value={p._id} disabled={isTrial}>
                      {p.name} — ₹{effectiveAmt?.toLocaleString()}
                      {isOverridden
                        ? ` (zone | global: ₹${p.amount?.toLocaleString()})`
                        : ""}{" "}
                      ({p.value} {p.unit}){isTrial ? " (Not allowed)" : ""}
                    </option>
                  );
                })}
              </select>

              {/* ✅ Zone price breakdown — selected plan choose பண்ணா show பண்றோம் */}
              {selectedPlanId &&
                (() => {
                  const selPlan = activePlans.find(
                    (p) => p._id === selectedPlanId,
                  );
                  if (!selPlan) return null;
                  const zoneEntry = driverZoneId
                    ? selPlan.zonePrices?.find((z) => z.zoneId === driverZoneId)
                    : null;
                  const effectiveAmt = zoneEntry
                    ? zoneEntry.amount
                    : selPlan.amount;
                  const isOverridden =
                    !!zoneEntry && zoneEntry.amount !== selPlan.amount;
                  return (
                    <div
                      style={{
                        marginTop: "0.5rem",
                        padding: "0.6rem 0.75rem",
                        borderRadius: "8px",
                        background: isOverridden ? "#f3f0ff" : "#f0fdf4",
                        border: `1px solid ${isOverridden ? "#c4b5fd" : "#86efac"}`,
                        fontSize: "0.8rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        {isOverridden ? "📍 Zone Price" : "💰 Plan Price"}
                        {isOverridden && (
                          <span
                            style={{ color: "#6b7280", marginLeft: "0.5rem" }}
                          >
                            (Global: ₹{selPlan.amount?.toLocaleString()})
                          </span>
                        )}
                      </span>
                      <strong
                        style={{
                          color: isOverridden ? "#7c3aed" : "#16a34a",
                          fontSize: "1rem",
                        }}
                      >
                        ₹{effectiveAmt?.toLocaleString()}
                      </strong>
                    </div>
                  );
                })()}
              {/* Zone label */}
              {driverZoneId && !selectedPlanId && (
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "#7c3aed",
                    marginTop: "0.4rem",
                  }}
                >
                  📍 Zone: {modalDriver?.zoneName || driverZoneId} —
                  zone-specific prices காட்டப்படுகின்றன
                </p>
              )}

              {/* Payment Method */}
              <label
                className="pc-notes-label"
                style={{ marginTop: "1rem", display: "block" }}
              >
                Payment Method
              </label>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  marginBottom: "1rem",
                }}
              >
                {["cash", "online"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setMethod(m)}
                    className={`filter-pill ${method === m ? "active" : ""}`}
                    style={{ flex: 1, textAlign: "center" }}
                  >
                    {m === "cash" ? "💵 Cash" : "💳 Online (Razorpay)"}
                  </button>
                ))}
              </div>

              {/* Online info banner */}
              {method === "online" && (
                <div
                  className="pc-info-banner"
                  style={{ marginBottom: "1rem" }}
                >
                  <span>ℹ️</span>
                  <p>
                    Razorpay checkout open ஆகும். Payment complete ஆனதும்
                    subscription automatically activate ஆகும் & Payments page-ல்
                    record show ஆகும்.
                  </p>
                </div>
              )}

              {/* Cash info banner */}
              {method === "cash" && (
                <div
                  className="pc-info-banner"
                  style={{ marginBottom: "1rem" }}
                >
                  <span>💡</span>
                  <p>
                    Cash pending record create ஆகும். Pending Cash page-ல் போய்
                    "Confirm Cash" பண்ணா subscription activate ஆகும்.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="pc-modal-actions">
                <button
                  className="pc-btn-cancel"
                  onClick={closeModal}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className="pc-btn-activate"
                  onClick={handleActivate}
                  disabled={busy || !selectedPlanId}
                >
                  {busy
                    ? "Processing…"
                    : method === "cash"
                      ? "✅ Create Cash Record"
                      : "💳 Open Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
